/***
 *
 *   MONDAY PDF SETTINGS
 *   The pure rules behind the PDF write-back card on the Monday settings page
 *   (monday_settings.js): which board columns can take the verification PDF,
 *   which stored column shows as selected, the per-board status line, and the
 *   body POST /api/integration/monday/boards/:board_id/pdf-column expects.
 *
 *   The server checks the column again on save (helper/mondaySettings.js
 *   isFileColumn), so this only decides what the page offers and shows.
 *
 *   Plain ESM with no imports, on purpose: test/monday-settings.js
 *   require()s this file to pin MONDAY_PDF_OPTIONS to the server registry.
 *
 **********/

// What the PDF can include beyond the summary page: the FYI PDF write-back's
// options, with its labels and defaults (server registry:
// helper/fyiPdfSettings.js). Proof of address is never offered.
export const MONDAY_PDF_OPTIONS = Object.freeze(
  [
    { key: "include_images", label: "Include images", defaultValue: true },
    { key: "include_sanctions", label: "Include PEP/Sanctions", defaultValue: false },
    { key: "include_adverse_media", label: "Include adverse media", defaultValue: false },
  ].map((option) => Object.freeze(option))
);

// The checkbox values from the settings response's pdf_writeback_options.
export function pdfOptionValues(stored) {
  const block = stored && typeof stored === "object" ? stored : {};
  return Object.fromEntries(
    MONDAY_PDF_OPTIONS.map((o) => [
      o.key,
      typeof block[o.key] === "boolean" ? block[o.key] : o.defaultValue,
    ])
  );
}

const FILE_COLUMN_TYPE = "file";

// The board's File columns, in board order.
export function fileColumns(columns) {
  return (Array.isArray(columns) ? columns : []).filter(
    (c) => c && c.type === FILE_COLUMN_TYPE
  );
}

// The select's value: the stored column when it is still a File column on
// the board, else "" (None). A deleted column shows as unselected.
export function selectedFileColumnId(columns, storedId) {
  if (!storedId) return "";
  return fileColumns(columns).some((c) => c.id === storedId) ? storedId : "";
}

// The per-board status line under the select, for the saved state.
export function boardPdfStatus({ columns, storedId }) {
  const files = fileColumns(columns);
  if (!files.length) {
    return { tone: "warn", text: "Add a File column to this board in monday first." };
  }
  if (!storedId) {
    return { tone: "warn", text: "Not uploading: choose a column." };
  }
  const saved = files.find((c) => c.id === storedId);
  if (!saved) {
    return {
      tone: "warn",
      text: "Not uploading: the saved column no longer exists. Choose another column.",
    };
  }
  return { tone: "ok", text: `Uploading to ${saved.title || saved.id}.` };
}

// "" (None) clears the column.
export function pdfColumnPayload(selectedId) {
  return { column_id: selectedId || null };
}
